import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs, { Dayjs } from "dayjs";

function getDefaultWeekRange(date: Dayjs) {
  let start = date.startOf("week").add(1, "day");
  if (start.day() !== 1) {
    start = start.subtract((start.day() + 6) % 7, "day");
  }
  const end = start.add(6, "day").endOf("day");
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

const today = dayjs();
const defaultView = "Week";
const { startDate, endDate } = getDefaultWeekRange(today);

interface SchedulingState {
  selectedDate: Dayjs;
  selectedView: "Day" | "Week" | "Month";
  scheduleViewType: "CALENDER" | "LIST";
  startDate: any;
  endDate: any;
  selectedLocation: any[];
  selectedProviderUuid: any[];
  selectedPatientUuid: any[];
  selectedAppointmentType: any;
  selectedStatuses: any[];
  isReload: boolean;
  isOpenFilter: boolean;
  isOpenForm: boolean;
  isOpenAppointmentDetails: {
    isOpen: boolean;
    appointmentUuid: string | null;
    occurrenceDate: string | null;
    viewOnly?: boolean;
  };
}

const initialState: SchedulingState = {
  selectedDate: dayjs(),
  selectedView: defaultView,
  scheduleViewType: "CALENDER",
  startDate,
  endDate,
  selectedLocation: [],
  selectedProviderUuid: [],
  selectedPatientUuid: [],
  selectedAppointmentType: [],
  selectedStatuses: [],
  isReload: false,
  isOpenFilter: false,
  isOpenForm: false,
  isOpenAppointmentDetails: {
    isOpen: false,
    appointmentUuid: null,
    occurrenceDate: null,
  },
};

const schedulingSlice = createSlice({
  name: "scheduling",
  initialState,
  reducers: {
    setSelectedDate: (state, action: PayloadAction<any>) => {
      state.selectedDate = action.payload;
    },
    setSelectedView: (state, action: PayloadAction<any>) => {
      state.selectedView = action.payload;
    },
    setScheduleViewType: (
      state,
      action: PayloadAction<"CALENDER" | "LIST">,
    ) => {
      state.scheduleViewType = action.payload;
    },
    setStartDate: (state, action: PayloadAction<any>) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action: PayloadAction<any>) => {
      state.endDate = action.payload;
    },
    setSelectedLocation: (state, action: PayloadAction<any[]>) => {
      state.selectedLocation = action.payload;
    },
    setSelectedProviderUuid: (state, action: PayloadAction<any>) => {
      state.selectedProviderUuid = action.payload;
    },
    setSelectedPatientUuid: (state, action: PayloadAction<any[]>) => {
      state.selectedPatientUuid = action.payload;
    },
    setSelectedAppointmentType: (state, action: PayloadAction<any[]>) => {
      state.selectedAppointmentType = action.payload;
    },
    setSelectedStatuses: (state, action: PayloadAction<any[]>) => {
      state.selectedStatuses = action.payload;
    },
    setIsReload: (state, action: PayloadAction<boolean>) => {
      state.isReload = action.payload;
    },
    setIsOpenFilter: (state, action: PayloadAction<boolean>) => {
      state.isOpenFilter = action.payload;
    },
    setIsOpenForm: (state, action: PayloadAction<boolean>) => {
      state.isOpenForm = action.payload;
    },
    setIsOpenAppointmentDetails: (state, action: PayloadAction<any>) => {
      state.isOpenAppointmentDetails = action.payload;
      if (!action.payload.isOpen) {
        state.isReload = true;
      }
    },
    reset: (state) => ({
      ...initialState,
      // Sidebar filter selections (patient/provider/status/appointment type)
      // are intentionally cleared when leaving the scheduling screen.
      // `selectedLocation` is preserved because it mirrors the globally
      // selected navbar location, not a scheduling-screen filter.
      selectedLocation: state.selectedLocation,
    }),
  },
});

export const {
  setSelectedDate,
  setSelectedView,
  setScheduleViewType,
  setStartDate,
  setEndDate,
  setSelectedLocation,
  setSelectedProviderUuid,
  setSelectedPatientUuid,
  setSelectedAppointmentType,
  setSelectedStatuses,
  setIsReload,
  setIsOpenFilter,
  setIsOpenForm,
  setIsOpenAppointmentDetails,
  reset,
} = schedulingSlice.actions;

export default schedulingSlice.reducer;
